/**
 * TrajectoryGoalEditor - UI for configuring trajectory (motion) goals
 *
 * Allows coaches to define movement-based constraints:
 * - Direction only: "must move forward"
 * - Template-relative: "must move as far as template"
 * - Travel distance: "must travel at least a share of how tall they look,
 *   measured from the coach's own demo" (TRAVEL_PR_A_CONTRACT.md)
 *
 * The body-relative mode ("must move 1 shoulder-width forward") and its
 * 15-unit `BodySegmentSelector` picker are DELETED (travel PR-B) — the
 * backend has refused the `body_threshold` field they wrote since PR-A, and
 * travel distance is the one body-size-relative movement check now.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, InformationCircleIcon, ShieldExclamationIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { Tooltip } from '@/components/ui';
import SpeedThresholdEditor from './SpeedThresholdEditor';
import TravelDistanceEditor from './TravelDistanceEditor';
import { formatScore } from '@/utils/formatters';
import type {
  TrajectoryConfig,
  DirectionType,
  AggregationType,
  TemplateComparisonOperator,
  DirectionConfig,
  TemplateRelativeThreshold,
  SpeedThreshold,
  TravelTarget,
} from '@/types/trajectory';
import { DIRECTION_LABELS } from '@/types/trajectory';

// =============================================================================
// Constants
// =============================================================================

// Only directions the backend can actually score.
//
// 'apart' and 'together' are deliberately absent: the DirectionType enum still
// carries them (so old templates deserialise), but get_direction_vector() has
// no vector for either and silently scores them as 'forward'. Offering them
// here meant a coach could pick a direction the evaluator would quietly
// substitute. Re-add them only once the backend maps them to real vectors.
const DIRECTION_OPTIONS: Array<{ value: DirectionType; label: string }> = [
  { value: 'forward', label: 'Forward' },
  { value: 'backward', label: 'Backward' },
  { value: 'up', label: 'Upward' },
  { value: 'down', label: 'Downward' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
];

const AGGREGATION_OPTIONS: Array<{ value: AggregationType; label: string; description: string }> = [
  { value: 'max_displacement', label: 'Maximum Reach', description: 'Furthest point from start' },
  { value: 'final_displacement', label: 'Final Position', description: 'Distance at end of movement' },
];

const TEMPLATE_COMPARISON_OPTIONS: Array<{ value: TemplateComparisonOperator; label: string; icon: string }> = [
  { value: 'same_as', label: 'Same As', icon: '≈' },
  { value: 'at_least', label: 'At Least', icon: '≥' },
  { value: 'at_most', label: 'At Most', icon: '≤' },
];

// =============================================================================
// Default Values
// =============================================================================

const DEFAULT_DIRECTION_CONFIG: DirectionConfig = {
  direction: 'forward',
  use_body_orientation: true,
  direction_threshold: 0.5,
};

const DEFAULT_TEMPLATE_THRESHOLD: TemplateRelativeThreshold = {
  comparison: 'same_as',
  target_ratio: 1.0,
  tolerance_percent: 15,
};

const DEFAULT_TRAJECTORY_CONFIG: TrajectoryConfig = {
  enabled: true,
  mode: 'direction_only',
  reference_type: 'phase_start',
  aggregation: 'max_displacement',
  direction: DEFAULT_DIRECTION_CONFIG,
  comparison: 'at_least',
  use_or_logic: true,
};

// =============================================================================
// Threshold Toggle Card Component
// =============================================================================

interface ThresholdToggleProps {
  label: string;
  description: string;
  icon: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  accentColor?: string;
}

function ThresholdToggle({ label, description, icon, checked, onChange, accentColor = 'purple' }: ThresholdToggleProps) {
  const colorClasses = {
    purple: {
      bg: checked ? 'bg-violet-500/15 border-violet-500/40' : 'bg-dark-card border-border-subtle hover:border-border-strong',
      iconBg: checked ? 'bg-violet-500/20 text-violet-400' : 'bg-dark-elevated-2 text-text-secondary',
      checkbox: 'text-violet-500 focus:ring-violet-500',
    },
    blue: {
      bg: checked ? 'bg-info/10 border-info/40' : 'bg-dark-card border-border-subtle hover:border-border-strong',
      iconBg: checked ? 'bg-info/20 text-info' : 'bg-dark-elevated-2 text-text-secondary',
      checkbox: 'text-blue-500 focus:ring-blue-500',
    },
  }[accentColor] || {
    bg: checked ? 'bg-violet-500/15 border-violet-500/40' : 'bg-dark-card border-border-subtle hover:border-border-strong',
    iconBg: checked ? 'bg-violet-500/20 text-violet-400' : 'bg-dark-elevated-2 text-text-secondary',
    checkbox: 'text-violet-500 focus:ring-violet-500',
  };

  return (
    <label
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-150',
        colorClasses.bg
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className={cn('h-4 w-4 mt-0.5 rounded border-border-strong', colorClasses.checkbox)}
      />
      <div className={cn('flex-shrink-0 p-1.5 rounded', colorClasses.iconBg)}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        <div className="text-xs text-text-secondary mt-0.5">{description}</div>
      </div>
    </label>
  );
}

// =============================================================================
// Template Threshold Editor Component
// =============================================================================

interface TemplateThresholdEditorProps {
  value: TemplateRelativeThreshold;
  onChange: (updates: Partial<TemplateRelativeThreshold>) => void;
}

function TemplateThresholdEditor({ value, onChange }: TemplateThresholdEditorProps) {
  const comparison = value.comparison ?? 'same_as';
  const targetRatio = value.target_ratio ?? 1.0;
  const tolerance = value.tolerance_percent ?? 15;

  return (
    <div className="space-y-4">
      {/* Comparison Operator Selector */}
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-2">
          Comparison Type
        </label>
        <div className="grid grid-cols-3 gap-2">
          {TEMPLATE_COMPARISON_OPTIONS.map((opt) => {
            const isSelected = comparison === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ comparison: opt.value })}
                className={cn(
                  'flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg border-2 transition-all duration-150',
                  isSelected
                    ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                    : 'bg-dark-card border-border-subtle text-text-secondary hover:border-border-strong hover:bg-dark-elevated-1'
                )}
              >
                <span className="text-lg font-mono">{opt.icon}</span>
                <span className="text-xs font-medium">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Target Percentage */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-text-secondary">
            Target
          </label>
          <span className="text-sm font-semibold text-violet-400">
            {formatScore(targetRatio)} of template
          </span>
        </div>
        <div className="relative">
          <input
            type="range"
            min="20"
            max="200"
            step="5"
            value={targetRatio * 100}
            onChange={(e) => onChange({ target_ratio: parseInt(e.target.value) / 100 })}
            className="w-full h-2 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer accent-violet-500"
          />
          <div className="flex justify-between text-[10px] text-text-muted mt-1">
            <span>20%</span>
            <span>100%</span>
            <span>200%</span>
          </div>
        </div>
      </div>

      {/* Tolerance (only for same_as) */}
      {comparison === 'same_as' && (
        <div className="p-3 bg-violet-500/10 rounded-lg border border-violet-500/20">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-text-secondary">
              Tolerance
            </label>
            <span className="text-sm font-semibold text-violet-400">
              ±{tolerance}%
            </span>
          </div>
          <input
            type="range"
            min="5"
            max="50"
            step="5"
            value={tolerance}
            onChange={(e) => onChange({ tolerance_percent: parseInt(e.target.value) })}
            className="w-full h-2 bg-violet-500/20 rounded-lg appearance-none cursor-pointer accent-violet-500"
          />
          <p className="text-[10px] text-violet-400 mt-2">
            Athlete must move between {Math.round(targetRatio * 100 * (1 - tolerance / 100))}% and {Math.round(targetRatio * 100 * (1 + tolerance / 100))}% of template distance
          </p>
        </div>
      )}

      {/* Summary for at_least / at_most */}
      {comparison === 'at_least' && (
        <p className="text-xs text-text-secondary bg-dark-elevated-1 p-2 rounded">
          Athlete must move at least {formatScore(targetRatio)} of what the template shows
        </p>
      )}
      {comparison === 'at_most' && (
        <p className="text-xs text-text-secondary bg-dark-elevated-1 p-2 rounded">
          Athlete must move at most {formatScore(targetRatio)} of what the template shows
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Component Props
// =============================================================================

interface TrajectoryGoalEditorProps {
  value: TrajectoryConfig | undefined;
  onChange: (config: TrajectoryConfig | undefined) => void;
  className?: string;
  hasPhases?: boolean;
  /** Travel-distance target wiring (TRAVEL_PR_A_CONTRACT.md) — the preview
   * endpoint is per-template, so this editor needs the saved template id. */
  templateId?: string;
  /** The tracked landmark (anatomical or lead_/trail_ functional name) —
   * same value MovementGoalAnnotation uses for `tracked_landmark`. */
  trackedLandmark?: string;
  /** Plain-English label for the tracked landmark, for copy. */
  trackedLandmarkLabel?: string;
  /** The phase this annotation is scoped to (contract §1.4 — the SAME
   * annotation-level `target_phase` every other goal type uses). `null`/
   * undefined = whole clip. */
  targetPhase?: string | null;
  /** Forwarded to `TravelDistanceEditor` — see its own prop docstring
   * (travel/preview-path-points: the demo-path overlay). */
  onPreviewTrajectoryChange?: (points: Array<[number, number, number]> | null) => void;
}

// =============================================================================
// Component
// =============================================================================

export default function TrajectoryGoalEditor({
  value,
  onChange,
  className,
  hasPhases: _hasPhases = true,
  templateId,
  trackedLandmark,
  trackedLandmarkLabel,
  targetPhase,
  onPreviewTrajectoryChange,
}: TrajectoryGoalEditorProps) {
  const [isExpanded, setIsExpanded] = useState(!!value?.enabled);

  // Derived state: which thresholds are enabled. `body_threshold` (and the
  // 15-unit BodySegmentUnit picker that set it) is deleted from the backend
  // schema (TRAVEL_PR_A_CONTRACT.md §1.3) — the authoring UI for it is
  // deleted below with it (travel PR-B, backlog item: studio polish). Only
  // `template_threshold` remains a live, user-editable threshold here.
  const hasTemplateThreshold = !!value?.template_threshold;

  // Travel-distance target (TRAVEL_PR_A_CONTRACT.md §1) — mutually
  // exclusive with every other mode/threshold field below. When set, the
  // evaluator takes an early dedicated path and ignores direction/
  // body_threshold/template_threshold/speed_threshold/aggregation entirely,
  // so the UI hides them rather than show controls that do nothing.
  const hasTravelTarget = !!value?.travel_target;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleToggle = useCallback((enabled: boolean) => {
    if (enabled) {
      onChange({ ...DEFAULT_TRAJECTORY_CONFIG });
      setIsExpanded(true);
    } else {
      onChange(undefined);
      setIsExpanded(false);
    }
  }, [onChange]);

  const handleDirectionChange = useCallback((direction: DirectionType) => {
    if (!value) return;
    onChange({
      ...value,
      direction: {
        ...DEFAULT_DIRECTION_CONFIG,
        ...value.direction,
        direction,
      },
    });
  }, [value, onChange]);

  const handleTemplateThresholdToggle = useCallback((enabled: boolean) => {
    if (!value) return;
    if (enabled) {
      onChange({
        ...value,
        mode: 'template_relative',
        template_threshold: DEFAULT_TEMPLATE_THRESHOLD,
      });
    } else {
      const { template_threshold: _template_threshold, ...rest } = value;
      onChange({
        ...rest,
        mode: 'direction_only',
      });
    }
  }, [value, onChange]);

  const handleTemplateThresholdChange = useCallback((updates: Partial<TemplateRelativeThreshold>) => {
    if (!value) return;
    onChange({
      ...value,
      template_threshold: {
        ...DEFAULT_TEMPLATE_THRESHOLD,
        ...value.template_threshold,
        ...updates,
      },
    });
  }, [value, onChange]);

  const handleSpeedThresholdChange = useCallback((speedThreshold: SpeedThreshold | undefined) => {
    if (!value) return;
    onChange({
      ...value,
      speed_threshold: speedThreshold,
    });
  }, [value, onChange]);

  const handleAggregationChange = useCallback((aggregation: AggregationType) => {
    if (!value) return;
    onChange({ ...value, aggregation });
  }, [value, onChange]);

  // Travel-distance target toggle. Enabling it clears is_gate (contract
  // §1.3: a travel target cannot be a gate — the backend 422s the pairing)
  // and the legacy body/template thresholds, since none of them are
  // consulted once travel_target is set. Disabling it drops the field
  // entirely so an abandoned toggle never persists a half-filled draft.
  const handleTravelTargetToggle = useCallback((enabled: boolean) => {
    if (!value) return;
    if (enabled) {
      const { body_threshold: _bt, template_threshold: _tt, is_gate: _ig, ...rest } = value;
      onChange({
        ...rest,
        travel_target: value.travel_target ?? { comparison: 'at_least' },
      });
    } else {
      const { travel_target: _travel, ...rest } = value;
      onChange({ ...rest });
    }
  }, [value, onChange]);

  const handleTravelTargetChange = useCallback((travelTarget: TravelTarget) => {
    if (!value) return;
    onChange({ ...value, travel_target: travelTarget });
  }, [value, onChange]);

  // ---------------------------------------------------------------------------
  // Summary Text
  // ---------------------------------------------------------------------------

  const summaryText = useMemo(() => {
    if (!value?.enabled) return '';

    // Travel-distance target: its own summary, independent of direction/
    // threshold state (none of which the evaluator consults once set).
    if (hasTravelTarget && value.travel_target) {
      const t = value.travel_target;
      if (t.value_body_heights != null) {
        return `Must travel at least ${t.value_body_heights.toFixed(2)} of how tall they look`;
      }
      return "Must travel at least what your own demo shows";
    }

    const direction = value.direction?.direction
      ? DIRECTION_LABELS[value.direction.direction]
      : '';

    const parts: string[] = [];

    // Direction-only case
    if (!hasTemplateThreshold) {
      return `Must move ${direction.toLowerCase()}`;
    }

    // Template threshold
    if (hasTemplateThreshold && value.template_threshold) {
      const comparison = value.template_threshold.comparison ?? 'same_as';
      const ratio = value.template_threshold.target_ratio ?? 1.0;
      const tolerance = value.template_threshold.tolerance_percent ?? 15;

      if (comparison === 'same_as') {
        parts.push(`${formatScore(ratio)} of template (±${tolerance}%)`);
      } else if (comparison === 'at_least') {
        parts.push(`at least ${formatScore(ratio)} of template`);
      } else {
        parts.push(`at most ${formatScore(ratio)} of template`);
      }
    }

    const thresholdText = parts.join('');

    return `Must move ${thresholdText}${direction ? ` ${direction.toLowerCase()}` : ''}`;
  }, [value, hasTemplateThreshold, hasTravelTarget]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={cn('border rounded-lg overflow-hidden', className)}>
      {/* Header */}
      <button
        type="button"
        onClick={() => {
          if (!value?.enabled) {
            handleToggle(true);
          } else {
            setIsExpanded(!isExpanded);
          }
        }}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 text-left transition-colors',
          value?.enabled
            ? 'bg-dark-elevated-2 hover:bg-dark-elevated-2 border-b border-border-subtle'
            : 'bg-dark-elevated-1 hover:bg-dark-elevated-2'
        )}
      >
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value?.enabled ?? false}
            onChange={(e) => {
              e.stopPropagation();
              handleToggle(e.target.checked);
            }}
            className="h-4 w-4 text-violet-500 rounded border-border-strong"
          />
          <span className="text-sm font-medium text-text-primary">
            Movement Goal
          </span>
          {!value?.enabled && (
            <span className="text-xs text-text-muted">(track motion over time)</span>
          )}
        </div>
        {value?.enabled && (
          isExpanded ? (
            <ChevronUpIcon className="h-4 w-4 text-text-secondary" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
          )
        )}
      </button>

      {/* Expanded Content */}
      {value?.enabled && isExpanded && (
        <div className="p-3 space-y-4">
          {/* Info Banner. Travel PR-B item 3: this used to say the same
              thing regardless of mode, including "measure by body
              proportions" — a control deleted below — and never mentioned
              the travel check at all. Now it says what the ACTIVE mode
              measures. */}
          <div className="flex items-start gap-2 p-2 bg-violet-500/10 rounded text-xs text-violet-400">
            <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              {hasTravelTarget ? (
                <>
                  <strong>Travel distance:</strong> how far the point you picked travels, measured
                  against how tall the person looks in their own video.
                </>
              ) : (
                <>
                  <strong>Movement Goal:</strong> checks how this point moves during the phase —
                  which direction it should go, or how far compared to your own demo.
                </>
              )}
            </span>
          </div>

          {/* Travel-distance target toggle (TRAVEL_PR_A_CONTRACT.md §1).
              Mutually exclusive with everything below — enabling it hides
              the legacy direction/threshold/speed/aggregation/gate controls
              and shows TravelDistanceEditor instead. */}
          <ThresholdToggle
            label="Travel distance"
            description="Set a target as a share of how tall the person looks, measured from your own demo"
            icon={<ArrowsPointingOutIcon className="h-4 w-4" />}
            checked={hasTravelTarget}
            onChange={handleTravelTargetToggle}
            accentColor="blue"
          />

          {hasTravelTarget && value.travel_target ? (
            <TravelDistanceEditor
              value={value.travel_target}
              onChange={handleTravelTargetChange}
              templateId={templateId}
              landmark={trackedLandmark}
              landmarkLabel={trackedLandmarkLabel}
              targetPhase={targetPhase}
              onPreviewTrajectoryChange={onPreviewTrajectoryChange}
            />
          ) : (
          <>
          {/* Direction Selection */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Direction
            </label>
            <select
              value={value.direction?.direction ?? 'forward'}
              onChange={(e) => handleDirectionChange(e.target.value as DirectionType)}
              className="w-full px-3 py-2 border border-border-subtle bg-dark-elevated-1 text-text-primary rounded-md text-sm focus:ring-violet-500 focus:border-violet-500"
            >
              {DIRECTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Distance Threshold Selection. The 15-unit body-segment picker
              (`BodySegmentSelector`/`body_threshold`) that used to live here
              is deleted (travel PR-B, plan §7.1/§7.2): the backend has
              refused `body_threshold` since PR-A (TRAVEL_PR_A_CONTRACT.md
              §1.3), and the travel-distance target above is the one entry
              point for a body-size-relative movement check now. */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Distance Thresholds
            </label>
            <div className="space-y-2">
              <ThresholdToggle
                label="Template-Based Distance"
                description="Compare movement to what the template shows"
                icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" /></svg>}
                checked={hasTemplateThreshold}
                onChange={handleTemplateThresholdToggle}
              />
            </div>
          </div>

          {/* Template-Relative Threshold Editor */}
          {hasTemplateThreshold && (
            <div className="space-y-3 p-3 bg-dark-elevated-1 rounded-lg border border-border-subtle">
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                <svg className="h-4 w-4 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" /></svg>
                Template-Based Distance
              </div>

              <TemplateThresholdEditor
                value={value.template_threshold ?? DEFAULT_TEMPLATE_THRESHOLD}
                onChange={handleTemplateThresholdChange}
              />
            </div>
          )}

          {/* Speed Threshold */}
          <SpeedThresholdEditor
            value={value.speed_threshold}
            onChange={handleSpeedThresholdChange}
          />

          {/* Aggregation (Advanced) */}
          <div>
            <label className="flex items-center gap-1 text-sm font-medium text-text-primary mb-1">
              Measure
              <Tooltip
                content={
                  <div className="max-w-xs text-sm">
                    <p className="font-semibold mb-1">Maximum Reach</p>
                    <p className="mb-2">Measures the furthest point the landmark reaches during the movement. Use for explosive movements like throws or jumps.</p>
                    <p className="font-semibold mb-1">Final Position</p>
                    <p>Measures where the landmark ends up at the end of the phase. Use for positioning checks like stance width.</p>
                  </div>
                }
              >
                <InformationCircleIcon className="h-4 w-4 text-text-muted cursor-help" />
              </Tooltip>
            </label>
            <select
              value={value.aggregation}
              onChange={(e) => handleAggregationChange(e.target.value as AggregationType)}
              className="w-full px-3 py-2 border border-border-subtle bg-dark-elevated-1 text-text-primary rounded-md text-sm focus:ring-violet-500 focus:border-violet-500"
            >
              {AGGREGATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-secondary">
              {AGGREGATION_OPTIONS.find(o => o.value === value.aggregation)?.description}
            </p>
          </div>

          {/* Gate Toggle - Required to Pass */}
          <div className="pt-3 border-t border-border-subtle">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldExclamationIcon className={cn(
                  'h-4 w-4',
                  value?.is_gate ? 'text-danger' : 'text-text-muted'
                )} />
                <label className="text-sm font-medium text-text-primary">
                  Required to Pass
                </label>
                <span className="text-xs text-text-muted">(Gate)</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={value?.is_gate ?? false}
                onClick={() => {
                  if (value) {
                    onChange({ ...value, is_gate: !value.is_gate });
                  }
                }}
                className={cn(
                  'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-2',
                  value?.is_gate ? 'bg-danger' : 'bg-dark-elevated-2'
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    value?.is_gate ? 'translate-x-4' : 'translate-x-0'
                  )}
                />
              </button>
            </div>
            {value?.is_gate && (
              <p className="mt-2 text-xs text-danger bg-danger/10 px-2 py-1.5 rounded">
                Athlete must pass this goal or receive automatic 0
              </p>
            )}
          </div>
          </>
          )}

          {/* Summary */}
          <div className="p-2.5 bg-violet-500/10 rounded-lg border border-violet-500/20">
            <div className="text-xs font-medium text-violet-400 mb-1">Summary</div>
            <div className="text-sm text-text-primary">{summaryText}</div>
          </div>
        </div>
      )}
    </div>
  );
}
